// Copyright (c) Ping. All rights reserved.
//
// RECOVERED-DEVICE side of the no-QR recovery peer-history hand-off.
//
// After this device restores from a recovery backup it holds its identity but
// NOT the pre-recovery plaintext of conversations it shares with others (MLS
// forward secrecy). Unlike linking — where the OLD device is online and ships
// history sealed to a QR-exchanged key — recovery has no QR and no single known
// sender. So this device ADVERTISES a fresh ephemeral X25519 delivery key
// (`POST /v1/devices/self/delivery-key`); peers that share a conversation detect
// the advert (`ensureRecoveredPeerDevicesAdmitted`), re-admit this device to the
// MLS group, and POST sealed history bundles to our inbox. We drain that inbox
// here.
//
// Per transfer the sender is UNKNOWN up front and there may be MULTIPLE peer
// senders, so we resolve + VERIFY each bundle independently:
//   bundle.conversation_id → our MLS roster (members) gives candidate accounts
//   → find the account whose device set includes the transfer's senderDeviceId
//   → GET that account's profile.user_identity_pubkey
//   → openHistoryBundle(sealed, deliveryPriv, senderIdentityPub) (verifies sig).
// A bundle we can't resolve/verify is DISCARDED — never replayed unverified.
//
// Durable + elastic, mirroring `pendingLinkingHistory.ts`: peers may admit + post
// over a long, staggered window (each peer triggers on conversation-open /
// foreground), so we persist the delivery PRIV across reloads and keep draining
// until an age cap. Verified events replay via the SHARED `replayHistoryBundle`
// with the same content-hash dedup the linking drain uses.

import { MessagingClient } from 'ping-openmls-sdk';

import { bytesToUrlB64NoPad, generateKemKeyPair, urlB64NoPadToBytes } from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { log } from '@core/log/log';
import {
  findAccountForDevice,
  getUserIdentityPubkey,
} from '@core/network/users';
import { db } from '@core/persistence/db';
import { unwrapSecretFromStorage, wrapSecretForStorage } from '@core/persistence/tokens';

import {
  consumeHistoryTransferInbox,
  type PendingHistoryTransfer,
  postSelfDeliveryKey,
} from './historyTransfersApi';
import { type HistoryBundleShape, replayHistoryBundle } from './pendingLinkingHistory';

interface OpenableClient {
  openHistoryBundle(
    sealed: Uint8Array,
    recipientPriv: Uint8Array,
    senderIdentityPub: Uint8Array,
  ): Promise<HistoryBundleShape>;
}

interface RosterClient {
  members?(id: Uint8Array): Promise<ReadonlyArray<{ user_id: Uint8Array; leaf_index: number }>>;
}

// Persisted state. The delivery PRIV is wrapped under the same AES-GCM key as
// the auth tokens (see tokens.ts) so it never sits in IndexedDB in the clear.
const STATE_KEY = '__recovery.deliveryPriv';
// Elastic drain window — peers admit + post over a long, staggered window
// (each triggers on conversation-open / foreground, not in a loop). Mirror the
// linking drain's budget: poll for several minutes per pass with backoff and
// resume across reloads until the age cap.
const POLL_ATTEMPTS = 24;
const POLL_INTERVAL_MS = 8000;
// Recovery window: keep advertising + draining for a generous span so peers who
// open a shared chat days later still complete the hand-off.
const RECOVERY_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

interface PersistedState {
  // base64url-nopad of the wrapped X25519 private key bytes is awkward to store
  // as a string; we wrap via tokens.ts which yields { iv, ciphertext } number
  // arrays of the base64url-nopad-encoded priv. Simpler: wrap the b64u string.
  wrappedPriv: { iv: number[]; ciphertext: number[] };
  pubB64Url: string;
  markedAt: number;
}

let draining = false;

function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

async function loadState(): Promise<PersistedState | null> {
  try {
    const rec = await db.settings_kv.get(STATE_KEY);
    const v = rec?.value as PersistedState | undefined;
    if (
      v === undefined ||
      typeof v.pubB64Url !== 'string' ||
      v.wrappedPriv === undefined ||
      !Array.isArray(v.wrappedPriv.iv) ||
      !Array.isArray(v.wrappedPriv.ciphertext)
    ) {
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

async function saveState(s: PersistedState): Promise<void> {
  try {
    await db.settings_kv.put({ key: STATE_KEY, value: s, updatedAt: Date.now() });
  } catch {
    /* best-effort — IndexedDB may be unavailable */
  }
}

async function clearState(): Promise<void> {
  try {
    await db.settings_kv.delete(STATE_KEY);
  } catch {
    /* best-effort */
  }
}

// One-shot "fresh MLS leaf" marker. Set by the session bootstrap whenever it
// mints a BRAND-NEW SDK identity on an authenticated load — i.e. a plain OTP
// re-login after a full-wipe sign-out (a fresh leaf that stored Welcomes no
// longer address), or a fresh signup. Consumed once by
// `ensureRecoveryDeliveryKeyAndDrain` to advertise this device's delivery key so
// peers immediately re-admit it to every shared conversation and ship the
// messages it missed while signed out. Mirrors iOS
// `PendingRecoveryPeerHistory.mark()` (see docs/ios-parity/11-logout-relogin-restore.md).
//
// SAFETY: only ever set when the identity did NOT survive (freshly minted), so it
// can never bait a peer into re-adding a device that is still a live MLS member
// (the duplicate-leaf/fork hazard the iOS gate guards against).
const FRESH_LEAF_KEY = '__recovery.freshLeafRelogin';

export async function markFreshLeafReloginPending(): Promise<void> {
  try {
    await db.settings_kv.put({ key: FRESH_LEAF_KEY, value: true, updatedAt: Date.now() });
  } catch {
    /* best-effort */
  }
}

async function consumeFreshLeafReloginMark(): Promise<boolean> {
  try {
    const rec = await db.settings_kv.get(FRESH_LEAF_KEY);
    if (rec?.value === true) {
      await db.settings_kv.delete(FRESH_LEAF_KEY);
      return true;
    }
  } catch {
    /* best-effort */
  }
  return false;
}

function liveRosterClient(): RosterClient | null {
  const session = useSessionStore.getState();
  if (session.status.kind !== 'authenticated') return null;
  return session.status.client as unknown as RosterClient;
}

/** Candidate sender ACCOUNT ids for a conversation = our MLS roster minus self,
 *  base64url(user_id) === accountId. Empty when the roster read is unavailable. */
async function candidateAccountsForConversation(
  conversationId: Uint8Array,
): Promise<string[]> {
  const client = liveRosterClient();
  if (client === null || typeof client.members !== 'function') return [];
  try {
    const roster = await client.members(conversationId);
    const out: string[] = [];
    const seen = new Set<string>();
    for (const m of roster) {
      const acct = bytesToUrlB64NoPad(m.user_id);
      if (acct.length > 0 && !seen.has(acct)) {
        seen.add(acct);
        out.push(acct);
      }
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Resolve + verify + replay a single inbound transfer. The sender is identified
 * only by the transfer's senderDeviceId, so we walk the bundle's conversation
 * roster to find which member account owns that device, fetch that account's
 * pinned identity pubkey, then open (which VERIFIES the signature). Returns the
 * number of replayed events, or 0 if the transfer was discarded (unresolvable /
 * unverifiable / non-renderable).
 */
async function handleTransfer(
  client: OpenableClient,
  deliveryPriv: Uint8Array,
  transfer: PendingHistoryTransfer,
): Promise<number> {
  const senderDeviceIdB64Url = bytesToUrlB64NoPad(transfer.senderDeviceId);

  // We don't know the conversation until we open the bundle, but opening needs
  // the sender identity pubkey to verify. Break the cycle: try each candidate
  // account across ALL conversations we're a member of. In practice the sender
  // shares exactly one conversation roster with us per transfer, so we gather
  // candidate accounts from every conversation and resolve the device owner.
  const rosterClient = liveRosterClient() as
    | (RosterClient & { listConversations?(): Promise<Array<{ id: Uint8Array }>> })
    | null;
  const candidateAccounts = new Set<string>();
  if (rosterClient !== null && typeof rosterClient.listConversations === 'function') {
    try {
      const convs = await rosterClient.listConversations();
      for (const c of convs) {
        for (const acct of await candidateAccountsForConversation(c.id)) {
          candidateAccounts.add(acct);
        }
      }
    } catch {
      /* fall through — empty candidate set discards the transfer */
    }
  }
  if (candidateAccounts.size === 0) {
    log.warn({ event: 'recovery.history.noCandidates' });
    return 0;
  }

  const senderAccount = await findAccountForDevice({
    candidateAccountIds: Array.from(candidateAccounts),
    deviceIdB64Url: senderDeviceIdB64Url,
  });
  if (senderAccount === null) {
    log.warn({ event: 'recovery.history.senderUnresolved' });
    return 0; // DISCARD — never replay an unverifiable transfer
  }

  const senderIdentityPubB64Url = await getUserIdentityPubkey(senderAccount);
  if (senderIdentityPubB64Url === null) {
    log.warn({ event: 'recovery.history.noSenderIdentity' });
    return 0; // DISCARD
  }
  const senderIdentityPub = urlB64NoPadToBytes(senderIdentityPubB64Url);

  try {
    // openHistoryBundle verifies the bundle signature against the sender's
    // pinned identity pubkey AND HPKE-decrypts to our delivery priv — either
    // failure throws and we discard (forged / not-for-us).
    const bundle = await client.openHistoryBundle(
      transfer.sealedBlob,
      deliveryPriv,
      senderIdentityPub,
    );
    const n = await replayHistoryBundle(bundle);
    log.info({
      event: 'recovery.history.replayed',
      messages: n,
      conversationPrefix: bytesToHex(bundle.conversation_id).slice(0, 8),
    });
    return n;
  } catch (err) {
    log.warn({ event: 'recovery.history.openFailed', message: (err as Error).message });
    return 0; // DISCARD on signature/decrypt failure
  }
}

/**
 * Drain (one elastic pass) the recovered device's history inbox: open + verify +
 * replay every resolvable transfer. Idempotent + in-flight guarded so the kick
 * on each authenticated transition (App.tsx) doesn't race the single destructive
 * inbox GET. Resumes from the persisted delivery key across reloads; clears the
 * persisted key once the recovery window ages out.
 */
async function drainRecoveryHistory(state: PersistedState): Promise<void> {
  if (draining) return;
  draining = true;
  const deliveryPriv = urlB64NoPadToBytes(
    (await unwrapSecretFromStorage(state.wrappedPriv)) ?? '',
  );
  if (deliveryPriv.length !== 32) {
    // Wrapper key missing / corrupt — can't open anything. Leave the persisted
    // state so a later load (with a readable wrapper key) retries, unless aged.
    if (Date.now() - state.markedAt > RECOVERY_MAX_AGE_MS) await clearState();
    draining = false;
    return;
  }
  const client = MessagingClient as unknown as OpenableClient;
  let totalReplayed = 0;
  let emptyStreak = 0;
  let sawAny = false;

  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    let transfers: PendingHistoryTransfer[];
    try {
      transfers = await consumeHistoryTransferInbox();
    } catch (err) {
      log.warn({ event: 'recovery.history.inboxFailed', message: (err as Error).message });
      transfers = [];
    }
    if (transfers.length === 0) {
      emptyStreak += 1;
      if (sawAny && emptyStreak >= 2) break;
    } else {
      sawAny = true;
      emptyStreak = 0;
      for (const t of transfers) {
        totalReplayed += await handleTransfer(client, deliveryPriv, t);
      }
    }
    if (attempt < POLL_ATTEMPTS - 1) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  }

  // Recovery has no single "done" signal (multiple peers post over a long
  // window), so we keep the persisted delivery key — and keep draining on each
  // load — until the recovery window ages out. Only then purge it.
  const aged = Date.now() - state.markedAt > RECOVERY_MAX_AGE_MS;
  if (aged) {
    await clearState();
    log.info({ event: 'recovery.history.windowEnded', messages: totalReplayed });
  }
  deliveryPriv.fill(0);
  draining = false;
}

/**
 * RECOVERED-DEVICE entry point, called ONCE post-recovery (RecoveryRestoreHost
 * stage-2 success → App.tsx authenticated effect when `recoveryRestoreRef` is
 * latched) AND idempotently on every authenticated load to resume the drain.
 *
 * First call (no persisted state): generate an ephemeral X25519 keypair, persist
 * the wrapped PRIV, advertise the PUB via `POST /v1/devices/self/delivery-key`,
 * then start draining. Subsequent calls: resume the drain off the persisted key.
 * No-ops past the recovery age cap.
 *
 * @param justRecovered  true on the post-recovery one-shot (forces advert even
 *   if no state yet); false on resume kicks (only continues an in-progress
 *   window). When omitted, resumes only if state already exists.
 */
export async function ensureRecoveryDeliveryKeyAndDrain(opts?: {
  justRecovered?: boolean;
}): Promise<void> {
  // A fresh-leaf re-login (plain OTP sign-in after a full-wipe sign-out) mints a
  // new leaf that pre-existing Welcomes no longer address. Treat it exactly like
  // the post-recovery one-shot: advertise a delivery key so peers re-admit us and
  // ship the history we missed. The marker is consumed here (one-shot) — the
  // persisted recovery `state` then drives resume on later loads.
  const freshLeafRelogin = await consumeFreshLeafReloginMark();
  const forceAdvertise = opts?.justRecovered === true || freshLeafRelogin;
  if (freshLeafRelogin) {
    log.info({ event: 'recovery.history.freshLeafReloginDetected' });
  }

  let state = await loadState();
  if (state === null) {
    if (!forceAdvertise) return; // nothing to resume
    // First post-recovery / fresh-leaf run: mint + advertise the delivery key.
    const kp = generateKemKeyPair();
    const pubB64Url = bytesToUrlB64NoPad(kp.publicKey);
    const wrappedPriv = await wrapSecretForStorage(bytesToUrlB64NoPad(kp.privateKey));
    state = { wrappedPriv, pubB64Url, markedAt: Date.now() };
    await saveState(state);
    kp.privateKey.fill(0);
    try {
      await postSelfDeliveryKey(pubB64Url);
      log.info({ event: 'recovery.history.deliveryKeyAdvertised' });
    } catch (err) {
      // Advert failed — keep the persisted key and let a later resume re-advert
      // (re-advertising the same key is idempotent server-side).
      log.warn({
        event: 'recovery.history.advertiseFailed',
        message: (err as Error).message,
      });
    }
  } else if (Date.now() - state.markedAt > RECOVERY_MAX_AGE_MS) {
    await clearState();
    return;
  } else if (opts?.justRecovered !== true) {
    // Resume kick: re-advertise (idempotent) in case the original advert failed
    // or the server-side row was lost, so peers can still find us.
    try {
      await postSelfDeliveryKey(state.pubB64Url);
    } catch {
      /* best-effort */
    }
  }
  await drainRecoveryHistory(state);
}

/**
 * LINKED-DEVICE delivery-key publish (server admission ledger).
 *
 * A freshly-linked device gets its QR-device history sealed to the QR ephemeral
 * key — known ONLY to the QR device. The server admission LEDGER lets ANY online
 * sibling admit this device and ship past history when the QR device is offline;
 * those siblings have no QR key, so they seal to a durable delivery key this
 * device advertises here (the SAME key shape the recovery path uses, persisted
 * under the same wrapped slot). We ONLY publish (mint-if-absent + advertise) — we
 * do NOT drain: while a linking hand-off is pending, `consumePendingLinkingHistory`
 * is the sole inbox drainer (trying both keys) so the two never race the single
 * destructive inbox. Re-advertising the same key is idempotent server-side.
 */
export async function publishDeliveryKeyForLinking(): Promise<void> {
  let state = await loadState();
  if (state === null) {
    const kp = generateKemKeyPair();
    const pubB64Url = bytesToUrlB64NoPad(kp.publicKey);
    const wrappedPriv = await wrapSecretForStorage(bytesToUrlB64NoPad(kp.privateKey));
    state = { wrappedPriv, pubB64Url, markedAt: Date.now() };
    await saveState(state);
    kp.privateKey.fill(0);
  }
  try {
    await postSelfDeliveryKey(state.pubB64Url);
    log.info({ event: 'linking.history.deliveryKeyAdvertised' });
  } catch (err) {
    // Advert failed — keep the persisted key; a later resume re-adverts (the
    // same key is idempotent server-side) so siblings can still find us.
    log.warn({ event: 'linking.history.advertiseFailed', message: (err as Error).message });
  }
}

/**
 * Load + unwrap the persisted history-delivery PRIV (32 bytes) so the linking
 * drain can try it as a second recipient key for ledger-sibling bundles. Returns
 * null when no delivery key has been published / the wrapper key is unreadable.
 */
export async function loadDeliveryPrivForLinking(): Promise<Uint8Array | null> {
  const state = await loadState();
  if (state === null) return null;
  const priv = urlB64NoPadToBytes((await unwrapSecretFromStorage(state.wrappedPriv)) ?? '');
  return priv.length === 32 ? priv : null;
}
